import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotifyProvider } from './context/NotifyContext';
import { SocketProvider } from './context/SocketContext';
import { MessagingUnreadProvider } from './context/MessagingUnreadContext';
import { ThemeProvider } from './context/ThemeContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import PageLoader from './components/PageLoader';
import './App.css';

// Set light theme on app load
document.documentElement.setAttribute('data-theme', 'light');

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Jobs Route Component - redirects to dashboard with appropriate tab
const JobsRoute = () => {
  const { user } = useAuth();
  
  // Redirect to dashboard with appropriate tab based on role
  if (user?.role === 'phlebotomist') {
    return <Navigate to="/dashboard?tab=available-jobs" replace />;
  }
  
  // For other roles, redirect to dashboard
  return <Navigate to="/dashboard" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <JobsRoute />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Landing />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <NotifyProvider>
        <AuthProvider>
          <SocketProvider>
            <MessagingUnreadProvider>
              <Router>
                <AppRoutes />
              </Router>
            </MessagingUnreadProvider>
          </SocketProvider>
        </AuthProvider>
      </NotifyProvider>
    </ThemeProvider>
  );
}

export default App;

